import { faInfoCircle, faTable } from '@fortawesome/pro-light-svg-icons';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { DialogHeader } from '../../../theme/components/dialog-header';
import { LoadingIcon } from '../../../theme/components/loading-icon';
import { PentairHeading } from '../../../theme/components/pentair-heading';
import { TableCellDate, TableCellInfo } from '../../../theme/components/table-cells';
import { useFetchQuotation } from './api/fetch';
import { useFetchQuotationList } from './api/list';
import { CartItemDescription } from './components/cart-item-description';

export const QuotationTable = () => {
  const [{ response, error, loading }, fetchQuotations] = useFetchQuotationList();
  const quotations = useMemo(() => response?.data?.listQuotations?.quotations || [], [response?.data]);


  const { t } = useTranslation();
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [selectedQuotation, setSelectedQuotation] = useState<string | null>(null);

  // Hold current page
  const [currentPage, setCurrentPage] = useState<number>(1);

  // Check for medium screen and add spaces
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));

  const showQuotationInfo = useCallback((quotationId: string) => {
    setSelectedQuotation(quotationId);
    setInfoDialogOpen(true);
  }, []);

  const paginatedQuotations = useMemo(() => {
    let perPage = 10;
    if (isSmallScreen) {
      perPage = 3;
    } else if (isMediumScreen) {
      perPage = 5;
    }

    const totalPages = Math.ceil(quotations.length / perPage);

    return {
      perPage,
      totalPages,
      isLastPage: currentPage === totalPages,
      list: quotations?.slice(0, currentPage * perPage),
    };
  }, [quotations, currentPage, isSmallScreen, isMediumScreen]);

  // handle the show more click event
  const handleShowMore = useCallback(() => {
    setCurrentPage((current) => current + 1);
  }, []);

  useEffect(() => {
    fetchQuotations({});
  }, [fetchQuotations]);

  if (loading) {
    return <LoadingIcon scale={0.75} />;
  }

  if (!quotations?.length) {
    return (
      <Typography>
        {t('ecommerce:emptyTable.quotations', { defaultValue: 'You have not requested any quotations yet.' })}
      </Typography>
    );
  }

  return (
    <>
      {error && <Alert severity="error">{error.message}</Alert>}
      <Box>
        <TableContainer
          sx={{
            /* TODO: Move to theme? */
            borderRight: isMediumScreen ? '3px solid #DADADA' : 'none',
          }}
        >
          <Table sx={{ marginBottom: 2 }}>
            <TableHead
              sx={{
                /* TODO: Move to theme? */
                whiteSpace: 'nowrap',
              }}
            >
              <TableRow>
                <TableCell>{t('ecommerce:tableHead.projectName', { defaultValue: 'Project name' })}</TableCell>
                <TableCell>{t('ecommerce:tableHead.country', { defaultValue: 'Country' })}</TableCell>
                <TableCell>{t('ecommerce:tableHead.status', { defaultValue: 'Status' })}</TableCell>
                <TableCell>{t('createdAt', { defaultValue: 'Created At' })}</TableCell>
                <TableCell>{t('updatedAt', { defaultValue: 'Last update' })}</TableCell>
                <TableCellInfo disabled />
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedQuotations.list?.map((quotation) => (
                <TableRow key={quotation.id}>
                  <TableCell>
                    {quotation?.id.substring(0, 4)} - {quotation.projectName}
                  </TableCell>
                  <TableCell>{quotation.shippingInfo?.country}</TableCell>
                  <TableCell>{quotation.status}</TableCell>
                  <TableCellDate width={140} value={quotation.createdAt} />
                  <TableCellDate width={140} value={quotation.updatedAt} />
                  <TableCellInfo onClick={() => showQuotationInfo(quotation.id)} />
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Button
          variant="text"
          color="secondary"
          onClick={handleShowMore}
          sx={{
            margin: 'auto',
            display: paginatedQuotations?.isLastPage ? 'none' : 'block',
          }}
          disableElevation={true}
        >
          Show More
        </Button>
      </Box>
      <Dialog open={infoDialogOpen} fullWidth maxWidth={'sm'}>
        <DialogHeader
          title={t('ecommerce:dialog.quotationDetails.title', { defaultValue: 'Quotation' })}
          subtitle={t('ecommerce:dialog.quotationDetails.subtitle', { defaultValue: 'Details' })}
          onClose={() => setInfoDialogOpen(false)}
        />
        <DialogContent>
          <QuotationInfo quotationId={selectedQuotation} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const QuotationInfo: React.FC<{ quotationId: string | undefined | null; }> = ({ quotationId }) => {
  const { t } = useTranslation();
  const [{ response, loading, error }, fetchQuotation] = useFetchQuotation();
  const quotation = response?.data?.quotation;
  const navigate = useNavigate();
  const { locale } = useParams();


  useEffect(() => {
    if (quotationId) {
      fetchQuotation({ id: quotationId });
    }
  }, [fetchQuotation, quotationId]);

  if (loading) {
    return <LoadingIcon scale={0.75} />;
  }

  return (
    <>
      {error && <Alert severity="error">{error?.message}</Alert>}
      <Box>
        <PentairHeading marginTop={1} icon={faInfoCircle}>
          {t('ecommerce:heading.quotationDetails', { defaultValue: 'Quotation details' })}:
        </PentairHeading>
        <Grid container columnSpacing={2}>
          <Grid item xs={12}>
            <Typography component="strong" variant="body2" color="silverGray.main" fontWeight={600}>
              {t('ecommerce:projectName.label', { defaultValue: 'Project name' })}:
            </Typography>
            <Typography paragraph variant="body2">
              {quotation?.projectName || 'No name provided.'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography component="strong" variant="body2" color="silverGray.main" fontWeight={600}>
              {t('ecommerce:contactPerson.label', { defaultValue: 'Contact person' })}:
            </Typography>

            <Typography variant="body2" paragraph>
              {quotation?.shippingInfo.firstName} {quotation?.shippingInfo.lastName}
              <br />
              {quotation?.shippingInfo.email}
              <br />
              {quotation?.shippingInfo.phone1}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography component="strong" variant="body2" color="silverGray.main" fontWeight={600}>
              {t('ecommerce:shippingAddress.label', { defaultValue: 'Shipping address' })}:
            </Typography>
            <Typography variant="body2" gutterBottom>
              {quotation?.shippingInfo.addressLine1}
              <br />
              {quotation?.shippingInfo.postal} {quotation?.shippingInfo.city}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={12}>
            <Typography component="strong" variant="body2" color="silverGray.main" fontWeight={600}>
              {t('ecommerce:projectDescription.label', { defaultValue: 'Project description' })}:
            </Typography>
            <Typography variant="body2" paragraph>
              {quotation?.projectDescription ||
                t('ecommerce:projectDescription.empty', { defaultValue: 'No description provided.' })}
            </Typography>

            <Typography variant="body2" gutterBottom>
              <Typography
                component="strong"
                variant="body2"
                color="silverGray.main"
                fontWeight={600}
                display={'inline'}
              >
                {t('ecommerce:termsAccepted.label', { defaultValue: 'Terms accepted' })}:
              </Typography>{' '}
              {quotation?.termsAccepted ? 'yes' : 'no'}
            </Typography>

            <Typography variant="body2">
              <Typography
                component="strong"
                variant="body2"
                color="silverGray.main"
                fontWeight={600}
                display={'inline'}
              >
                {t('ecommerce:subscribedToNewsAndUpdates.label', {
                  defaultValue: 'Subscribe to news and updates',
                })}
                :
              </Typography>{' '}
              {quotation?.subscribeToNewsAndUpdates ? 'yes' : 'no'}
            </Typography>
          </Grid>
        </Grid>

        <PentairHeading icon={faTable} sx={{ marginBottom: 0 }}>
          {t('ecommerce:heading.quotationItems', { defaultValue: 'Quotation items' })}:
        </PentairHeading>

        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('ecommerce:tableHead.quantity', { defaultValue: '#' })}</TableCell>
              <TableCell>{t('ecommerce:tableHead.product', { defaultValue: 'Product' })}</TableCell>
              <TableCellInfo disabled />
            </TableRow>
          </TableHead>
          <TableBody>
            {quotation?.cart.items?.map((item) => (
              <TableRow sx={{ verticalAlign: 'top' }} key={item.sku}>
                <TableCell>{item?.quantity}x</TableCell>
                <TableCell>
                  <CartItemDescription title={item.sku + ' - ' + item.title} cartItem={item} />
                </TableCell>
                <TableCellInfo onClick={() => navigate(`/${locale}/valve/${item.product?.sku}`)} />
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </>
  );
};
